import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import CheckIcon from "@mui/icons-material/Check";
import { Button } from "components/common/Button";
import { SectionRule } from "components/common/SectionRule";
import { useCreateModel } from "@/components/main/utils/models/useCreateModel";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";

/// What `OrgCreatePage` hands this page through router state. The org id is
/// also in the path, so a reload keeps the page; only the space name is
/// state-only, and it degrades to the org's first space.
export interface CreatedOrgNotice {
  org_id: string;
  name: string;
  first_space_name: string;
}

/// A location state carrying a freshly created org, or nothing.
export const createdOrgFrom = (state: unknown): CreatedOrgNotice | null => {
  const notice = (state as { createdOrg?: CreatedOrgNotice } | null)
    ?.createdOrg;
  return notice?.org_id ? notice : null;
};

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex min-h-8 items-center gap-2 px-2">
    <span className="w-36 shrink-0 text-fg-muted-regular-small">{label}</span>
    <span className="min-w-0 text-fg-regular">{children}</span>
  </div>
);

const Next: React.FC<{
  title: string;
  detail: string;
  onClick: () => void;
}> = ({ title, detail, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-1 flex-col gap-1 rounded-app border border-border p-3 text-left hover:bg-list-hover"
  >
    <span className="text-fg-medium">{title}</span>
    <span className="text-fg-muted-regular-small">{detail}</span>
  </button>
);

/// The page you land on after creating an org — its own route, so it is a
/// screen with an address and a deliberate exit rather than a banner wedged
/// into the Overview. `Go to Overview` is the way out; the Next cards leave
/// too, by going where they say.
///
/// The org is already the active context by the time this renders
/// (`OrgCreatePage` switches before navigating), so the sidebar behind it is
/// already the new org's.
export const OrgCreatedPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { org_id: orgId } = useParams();

  const orgs = useOrgContextStore((s) => s.orgs);
  const spaces = useOrgContextStore((s) => s.spaces);
  const plan = useOrgContextStore((s) => s.plan);
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const switchOrg = useOrgContextStore((s) => s.switchOrg);
  // Same action the Overview and team lists use, so the new model lands in
  // this org's create-space and plays the identical settle-in animation.
  const { createModel } = useCreateModel();

  const fromState = createdOrgFrom(location.state);
  const org = orgs.find((o) => o.org_id === orgId);
  const name = fromState?.name ?? org?.name ?? null;
  // State-only on a fresh create; on a reload the org's own first space is the
  // same space, so the fact stays true either way.
  const firstSpace = fromState?.first_space_name ?? spaces[0]?.name ?? null;

  // Reachable by URL, so the context follows the page rather than the reverse:
  // a reload must not leave the sidebar on whatever org was last active. The
  // id is trusted when it came from our own create response (router state),
  // even if the org list has not caught up yet.
  useEffect(() => {
    if (orgId && (org || fromState) && activeOrgId !== orgId)
      void switchOrg(orgId);
  }, [orgId, org, fromState, activeOrgId, switchOrg]);

  // Someone else's org (or a deleted one) has nothing to confirm — but only
  // judge that on a cold arrival: right after a create the list may not list
  // the new org yet, and bouncing then would eat the confirmation.
  useEffect(() => {
    if (!orgId || (!fromState && orgs.length > 0 && !org))
      navigate("/overview", { replace: true });
  }, [orgId, org, fromState, orgs.length, navigate]);

  if (!orgId || !name) return null;

  return (
    <div className="flex h-full flex-col overflow-y-auto px-8">
      <div
        className="mx-auto flex w-full max-w-4xl flex-col gap-6 pb-6"
        style={{ paddingTop: "10vh" }}
      >
        <span
          className="font-mono text-fg-muted"
          style={{ fontSize: "var(--font-size-x-small)" }}
        >
          Settings / New organization / Created
        </span>

        <div className="flex items-start gap-3 px-2">
          <span
            className="flex shrink-0 items-center justify-center rounded-full border border-success"
            style={{ width: "1.75rem", height: "1.75rem" }}
          >
            <CheckIcon sx={{ fontSize: 14 }} className="text-success" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="truncate text-fg-title">{name}</span>
            <span className="text-fg-muted-regular">
              You are now working inside it; the sidebar has switched context.
            </span>
          </div>
        </div>

        <section className="flex flex-col">
          <SectionRule label="What was created" />
          <Fact label="Organization ID">
            <span
              className="font-mono text-fg-muted"
              style={{ fontSize: "var(--font-size-small)" }}
            >
              {orgId}
            </span>
          </Fact>
          <Fact label="Plan period">
            {plan?.name ?? "Free"} · opened just now
          </Fact>
          {firstSpace && <Fact label="First space">{firstSpace}</Fact>}
        </section>

        <section className="flex flex-col">
          <SectionRule label="Next" />
          <div className="flex gap-2 px-2">
            <Next
              title="Create a model"
              detail={
                firstSpace
                  ? `Created in ${firstSpace} unless another space is selected.`
                  : "Created in your first space unless another is selected."
              }
              onClick={() => {
                void createModel();
                navigate("/overview", { replace: true });
              }}
            />
            <Next
              title="Invite people"
              detail="Members panel. Each member who joins receives their own space."
              onClick={() => navigate(`/organizations/${orgId}#members`)}
            />
            <Next
              title="Add a connection"
              detail="Connections are configured per space, not per organization."
              onClick={() => navigate("/connections")}
            />
          </div>
        </section>

        <div className="flex justify-end px-2">
          <Button
            variant="primary"
            onClick={() => navigate("/overview", { replace: true })}
          >
            Go to Overview
          </Button>
        </div>
      </div>
    </div>
  );
};
